import { useEffect, useMemo } from 'react';
import { Check, Close, CopyAll } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, FormLabel, Paper, PaperProps, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Action, useScheduledActionQuery } from '../../../../api/gql';
import { DataEntry } from '../../../../components/DataEntry';
import { ExpandedBox } from '../../../../components/ExpandedBox';
import { FormButtonToggleGroup } from '../../../../components/form/FormButtonToggleGroup';
import { FormInput } from '../../../../components/form/FormInput';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { ActionIcon } from '../../../../components/invoice/ScheduledActionIcon';
import { useClipboard } from '../../../../hooks/useClipboard';
import { getActionTransKey } from '../../../../utils/invoice';
import { notify } from '../../../../utils/notification';
import { useUpdateScheduledAction } from '../hooks/useUpdateScheduledAction';

const TRANS_NS = 'page-admin-task-detail';

type ScheduledActionFormType = {
  status: string;
  taskSuccess?: string | null;
  internalNote?: string | null;
  note?: string | null;
};
export const ScheduledActionControl = ({ taskId, sx, ...rest }: { taskId: string } & Omit<PaperProps, 'children'>) => {
  const { t } = useTranslation(TRANS_NS);
  const { copyToClipboard, copyRichTextToClipboard } = useClipboard();
  const { control, reset, handleSubmit, watch } = useForm<ScheduledActionFormType>();

  const status = watch('status');

  const { data, isLoading, error } = useScheduledActionQuery(
    { id: taskId },
    {
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (!data) {
      return;
    }

    reset({
      internalNote: data.scheduledAction.internalNote,
      note: data.scheduledAction.note,
      status: data.scheduledAction.doneAt ? '1' : '0',
      // null == undefined je true
      taskSuccess: data.scheduledAction.success == null ? null : data.scheduledAction.success ? '1' : '0',
    });
  }, [data, reset]);

  useEffect(() => {
    if (error) {
      console.error(error);
      notify.negative({ message: t('query.error') });
    }
  }, [error, t]);

  const { update, isPending: isMutating } = useUpdateScheduledAction({
    onSuccess: () => {
      // navigate(-1);
      notify.positive({ message: t('mutation.success') });
    },
    onError: () => notify.positive({ message: t('mutation.error') }),
  });

  const messageTemplate = useMemo(() => {
    return data?.scheduledAction.recommendedMessage;
  }, [data?.scheduledAction]);

  const subjectTemplate = useMemo(() => {
    return data?.scheduledAction.recommendedSubject;
  }, [data?.scheduledAction]);

  const onSubmit = (data: ScheduledActionFormType) => {
    const isSuccessRelevant = data.status === '1';
    const success = isSuccessRelevant ? (data.taskSuccess ? data.taskSuccess === '1' : null) : null;
    update({
      input: {
        id: taskId,
        doneAt: data.status === '1' ? new Date().toISOString() : null,
        success: success,
        note: data.note,
        internalNote: data.internalNote,
      },
    });
  };
  const isAutomatized = useMemo(() => data?.scheduledAction.action !== Action.Call, [data?.scheduledAction.action]);
  const isDisabled = useMemo(
    () => isAutomatized && !data?.scheduledAction.doneAt,
    [isAutomatized, data?.scheduledAction.doneAt],
  );
  return (
    <Paper
      id={'scheduledActionControl'}
      sx={{
        p: { md: 4, xs: 2 },
        flex: 1,
        minHeight: 645,
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
      {...rest}
    >
      {isLoading ? <FullSizeLoader /> : null}
      {error ? (
        <ExpandedBox sx={{ backdropFilter: 'blur(2px)', zIndex: 10 }}>
          <Typography>{t('query.error')}</Typography>
        </ExpandedBox>
      ) : null}
      <Typography variant={'h2'}>{t('task')}</Typography>

      <Stack direction={'row'} flexWrap={'wrap'} gap={8} mt={4}>
        <DataEntry
          flex={1}
          spacing={1}
          title={t('taskType')}
          text={
            data?.scheduledAction.action ? (
              <Stack direction={'row'} gap={1}>
                <ActionIcon color={'primary'} action={data.scheduledAction.action} />
                <Typography>{t(getActionTransKey(data.scheduledAction.action))}</Typography>
              </Stack>
            ) : (
              ''
            )
          }
        />
        <DataEntry
          flex={1}
          spacing={1}
          title={t('scheduledAt')}
          text={
            data?.scheduledAction.scheduledAt ? new Date(data?.scheduledAction.scheduledAt).toLocaleDateString() : ''
          }
        />
      </Stack>

      {subjectTemplate ? (
        <Box sx={{ mt: 4 }}>
          <FormLabel>{t('recommendedSubject')}</FormLabel>
          <Box sx={{ py: 3, px: 2, mt: 1, bgcolor: 'background.default' }}>
            <Typography whiteSpace={'break-spaces'}>{subjectTemplate}</Typography>

            <Stack mt={2} direction={'row'} justifyContent={'flex-end'}>
              <Button startIcon={<CopyAll />} variant={'outlined'} onClick={() => copyToClipboard(subjectTemplate)}>
                {t('copyRecommendedSubject')}
              </Button>
            </Stack>
          </Box>
        </Box>
      ) : null}

      {messageTemplate ? (
        <Box sx={{ mt: 4 }}>
          <FormLabel>{t('recommendedMessage')}</FormLabel>
          <Box sx={{ py: 3, px: 2, mt: 1, bgcolor: 'background.default' }}>
            <Typography whiteSpace={'break-spaces'} dangerouslySetInnerHTML={{ __html: messageTemplate }}></Typography>

            {data?.scheduledAction.action !== Action.Call ? (
              <Stack mt={2} direction={'row'} justifyContent={'flex-end'}>
                <Button
                  startIcon={<CopyAll />}
                  variant={'outlined'}
                  onClick={() =>
                    data?.scheduledAction.action === Action.Email
                      ? copyRichTextToClipboard(messageTemplate)
                      : copyToClipboard(messageTemplate)
                  }
                >
                  {t('copyRecommendedMessage')}
                </Button>
              </Stack>
            ) : null}
          </Box>
        </Box>
      ) : null}

      {isDisabled ? (
        <Box mt={4}>
          <FormLabel>{t('effectuation')}</FormLabel>
          <Box sx={{ py: 3, px: 2, mt: 1 }}>
            <Typography align={'center'} fontWeight={'bold'}>
              {t('thisTaskIsAutomatic')}
            </Typography>
            <Typography align={'center'} mt={1}>
              {t('afterScheduledTimeYouCanEdit', {
                time: data?.scheduledAction.scheduledAt
                  ? format(new Date(data.scheduledAction.scheduledAt), 'dd.MM.yyyy hh:mm')
                  : '',
              })}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={'row'} flexWrap={'wrap'} gap={8} mt={4}>
          <FormButtonToggleGroup
            toggleButtonGroupProps={{
              disabled: isDisabled,
            }}
            control={control}
            name={'status'}
            label={t('taskStatus')}
            options={[
              {
                value: '0',
                color: 'error',
                children: (
                  <Stack direction={'row'} spacing={1}>
                    <Close />
                    <Typography>{t('taskStatusFail')}</Typography>
                  </Stack>
                ),
              },
              {
                value: '1',
                color: 'success',
                children: (
                  <Stack direction={'row'} spacing={1}>
                    <Check />
                    <Typography>{t('taskStatusSuccess')}</Typography>
                  </Stack>
                ),
              },
            ]}
          />

          <FormButtonToggleGroup
            control={control}
            name={'taskSuccess'}
            label={t('taskSuccess')}
            toggleButtonGroupProps={{
              disabled: isDisabled || status === '0',
            }}
            options={[
              {
                value: '0',
                color: 'error',
                children: (
                  <Stack direction={'row'} spacing={1}>
                    <Close />
                    <Typography ml={1}>{t('unsuccessfully')}</Typography>
                  </Stack>
                ),
              },
              {
                value: '1',
                color: 'success',
                children: (
                  <Stack direction={'row'} spacing={1}>
                    <Check />
                    <Typography ml={1}>{t('successfully')}</Typography>
                  </Stack>
                ),
              },
            ]}
          />
        </Stack>
        <Stack mt={4} spacing={2}>
          <FormInput
            control={control}
            name={'internalNote'}
            disabled={isDisabled}
            multiline={true}
            minRows={3}
            label={t('internalNote')}
          />
          <FormInput
            control={control}
            name={'note'}
            disabled={isDisabled}
            multiline={true}
            minRows={3}
            label={t('vendorNote')}
          />
        </Stack>
        <Stack direction={'row'} justifyContent={'center'} mt={6}>
          <LoadingButton
            disabled={isDisabled}
            loading={isMutating}
            variant={'contained'}
            size={'large'}
            startIcon={<Check />}
            type={'submit'}
          >
            {t('completeTask')}
          </LoadingButton>
        </Stack>
      </form>
    </Paper>
  );
};
